import React, { useEffect, useState, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const Countries = () => {
  const { user } = useContext(AuthContext);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', status: 'active' });
  
  const fetchCountries = async () => {
    try {
      const res = await api.get('/countries');
      setCountries(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await api.put(`/countries/${formData.id}`, { name: formData.name, status: formData.status });
      } else {
        await api.post('/countries', { name: formData.name, status: formData.status });
      }
      fetchCountries();
      setShowForm(false);
      setFormData({ id: null, name: '', status: 'active' });
    } catch (err) {
      alert(err.response?.data?.details?.[0] || err.response?.data?.error || 'Error saving country');
    }
  };

  const handleEdit = (country) => {
    setFormData({ id: country.id, name: country.name, status: country.status });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this country? Applications referencing it might break if no cascading.')) {
        try {
            await api.delete(`/countries/${id}`);
            fetchCountries();
        } catch (err) {
            alert('Error deleting country. It might be referenced by applications.');
        }
    }
  };

  const toggleStatus = async (country) => {
    try {
      const newStatus = country.status === 'active' ? 'inactive' : 'active';
      await api.put(`/countries/${country.id}`, { name: country.name, status: newStatus });
      fetchCountries();
    } catch (err) {
      alert('Error changing status');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Manage Countries</h1>
        <button className="btn-primary" onClick={() => {
            setShowForm(!showForm);
            setFormData({ id: null, name: '', status: 'active' });
        }}>
          {showForm ? 'Cancel' : '+ New Country'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-8 border-l-4 border-indigo-500">
          <h2 className="text-lg font-bold mb-4">{formData.id ? 'Edit' : 'Add'} Country</h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md">
            <div>
              <label className="block text-sm font-medium mb-1">Country Name</label>
              <input type="text" className="input-field" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required placeholder="e.g. Australia" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select className="input-field" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex justify-end mt-2">
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b text-gray-500 text-sm">
            <tr>
              <th className="p-4">Country</th>
              <th className="p-4">Status</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {countries.map(c => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="p-4 font-semibold text-gray-800">{c.name}</td>
                <td className="p-4">
                  <button 
                    onClick={() => toggleStatus(c)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                  >
                    {c.status.toUpperCase()}
                  </button>
                </td>
                <td className="p-4 flex gap-3">
                  <button onClick={() => handleEdit(c)} className="text-blue-600 hover:text-blue-800">Edit</button>
                  {user?.role === 'Admin' && (
                    <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  )}
                </td>
              </tr>
            ))}
            {countries.length === 0 && (
                <tr><td colSpan="3" className="p-4 text-center text-gray-500">No countries found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Countries;
